import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../experience/api";
import { DISH_PHOTOS, normName } from "../table/dishPhotos";
import { VariantCard } from "../table/screens";
import { DishArt, addToSharedCart } from "./LandingV2";
import "../table/table.css";
import "./landing.css";

const CatThumb = ({ cat, dishes }) => {
  const [err, setErr] = useState(false);
  const d = dishes.find((x) => x.category === cat && x.img);
  if (!d || err) return <span className="v2-noph" style={{ width: 84, height: 84 }}><span>{cat}</span></span>;
  return (
    <span className="v2-ph" style={{ width: 84, height: 84 }}>
      <img src={d.img} alt={cat} loading="lazy" onError={() => setErr(true)} />
    </span>
  );
};

export default function DarkMenu() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("c") || "";
  const [dishes, setDishes] = useState(null);
  const [cats, setCats] = useState([]);
  const [varGroups, setVarGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [cartCount, setCartCount] = useState(() => {
    try { return Object.values(JSON.parse(localStorage.getItem("up_cart") || "{}")).reduce((n, i) => n + i.qty, 0); } catch { return 0; }
  });

  useEffect(() => {
    api.get("/table-menu").then((r) => {
      setCats(r.data.categories || []);
      setVarGroups(r.data.variation_groups || []);
      setDishes((r.data.dishes || []).map((d) => ({ ...d, img: d.image || DISH_PHOTOS[normName(d.name)] || null })));
    }).catch(() => setDishes([]));
    window.scrollTo(0, 0);
  }, []);

  const list = useMemo(() => {
    if (!dishes) return [];
    const q = query.trim().toLowerCase();
    if (q) return dishes.filter((d) => d.name.toLowerCase().includes(q)).slice(0, 40);
    if (activeCat) return dishes.filter((d) => d.category === activeCat);
    return [];
  }, [dishes, query, activeCat]);

  const memberOf = useMemo(() => {
    const mo = {};
    (varGroups || []).forEach((g) => g.options.forEach((o) => { mo[o.slug] = g; }));
    return mo;
  }, [varGroups]);

  const rows = useMemo(() => {
    const out = [];
    const used = new Set();
    for (const d of list) {
      const g = memberOf[d.slug];
      if (g) {
        if (!used.has(g.group_id)) { used.add(g.group_id); out.push({ type: "group", g, initial: d.slug }); }
      } else out.push({ type: "dish", d });
    }
    return out;
  }, [list, memberOf]);

  const catCards = useMemo(() => cats.map((c) => {
    const ds = (dishes || []).filter((d) => d.category === c);
    return { name: c, count: ds.length };
  }).filter((c) => c.count > 0), [cats, dishes]);

  const add = (d) => {
    addToSharedCart(d);
    setCartCount((n) => n + 1);
    toast.success(`${d.name} added`, { style: { background: "#221408", color: "#F4A300", border: "1px solid #D4AF37" } });
  };

  const addVariant = (dish, label) => {
    let cart = {};
    try { cart = JSON.parse(localStorage.getItem("up_cart") || "{}"); } catch { cart = {}; }
    cart[dish.slug] = { slug: dish.slug, name: label, price: dish.price, image: dish.img || null, qty: (cart[dish.slug]?.qty || 0) + 1 };
    localStorage.setItem("up_cart", JSON.stringify(cart));
    setCartCount((n) => n + 1);
    toast.success(`${label} added`, { style: { background: "#221408", color: "#F4A300", border: "1px solid #D4AF37" } });
  };

  const showList = query.trim() || activeCat;
  return (
    <div className="v2-root">
      <div className="v2-wrap" style={{ paddingTop: 18 }}>
        <div className="dm-head">
          <button className="dm-back" data-testid="dm-back" onClick={() => (activeCat && !query ? setParams({}) : navigate("/"))}>
            {activeCat && !query ? "All categories" : "Home"}
          </button>
          <h1 className="dm-title" data-testid="dm-title">{activeCat && !query ? activeCat : "Full Menu"}</h1>
        </div>
        <input className="dm-search" data-testid="dm-search" placeholder="Search the menu" value={query} onChange={(e) => setQuery(e.target.value)} />

        {dishes === null && <p style={{ color: "#9b8a68", fontSize: 13 }}>Lighting the tandoor…</p>}

        {dishes !== null && !showList && (
          <div className="dm-grid" data-testid="dm-cat-grid">
            {catCards.map((c, i) => (
              <button key={c.name} className="dm-cat" data-testid={`dm-cat-${i}`} style={{ animationDelay: `${Math.min(i * 0.06, 0.5)}s`, "--sx": i % 2 ? "36px" : "-36px" }}
                onClick={() => { setParams({ c: c.name }); window.scrollTo(0, 0); }}>
                <CatThumb cat={c.name} dishes={dishes} />
                <h3>{c.name}</h3>
                <p>{c.count} dish{c.count > 1 ? "es" : ""}</p>
              </button>
            ))}
          </div>
        )}

        {dishes !== null && showList && (
          <div data-testid="dm-dish-list">
            {rows.map((r, i) => r.type === "group" ? (
              <div key={`g-${r.g.group_id}`} data-testid={`dm-group-${r.g.group_id}`} style={{ marginBottom: 10, animation: "v2-slidein 0.45s ease both", animationDelay: `${Math.min(i * 0.04, 0.4)}s` }}>
                <VariantCard g={r.g} dishes={dishes} addVariant={addVariant} initialSlug={r.initial} />
              </div>
            ) : (
              <div className="dm-row" key={r.d.slug} data-testid={`dm-dish-${r.d.slug}`} style={{ animationDelay: `${Math.min(i * 0.04, 0.4)}s` }}>
                <DishArt d={r.d} size={66} />
                <div className="inf">
                  <p className="nm">{r.d.name}</p>
                  {r.d.description && <p className="ds">{r.d.description}</p>}
                  <p className="pr">${Number(r.d.price).toFixed(2)}</p>
                </div>
                <button className="dm-add" data-testid={`dm-add-${r.d.slug}`} onClick={() => add(r.d)}>+ Add</button>
              </div>
            ))}
            {rows.length === 0 && <p style={{ color: "#9b8a68", fontSize: 13 }}>Nothing matches. Try another craving.</p>}
          </div>
        )}
      </div>
      {cartCount > 0 && (
        <button className="v2-cartchip" data-testid="dm-cart-chip" onClick={() => navigate("/go/cart")}>
          View cart · {cartCount} item{cartCount > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}
